import { useTranslation } from '@numistic/i18n'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export interface LightboxImage {
  id: string
  /** Облегчённая копия (WebP 1600 px) — показывается сразу. */
  display: string
  /** Оригинал — подгружается, когда фото приближают. */
  original: string
  alt: string
  caption?: string
}

const MIN = 1
const MAX = 8
const STEP = 1.5
/** С какого увеличения подгружать оригинал. */
const HI_RES_FROM = 1.3

type Point = { x: number; y: number }

/**
 * Просмотр фото поверх страницы: колесо/щипок/двойной клик — зум (к точке), перетаскивание —
 * сдвиг, стрелки и свайп — соседние фото, Esc — закрыть.
 */
export function Lightbox({
  images,
  index,
  onIndexChange,
  onClose,
}: {
  images: LightboxImage[]
  index: number
  onIndexChange: (index: number) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const image = images[index]
  const [scale, setScale] = useState(1)
  const [offset, setOffset] = useState<Point>({ x: 0, y: 0 })
  const [hiRes, setHiRes] = useState<string | null>(null)
  const stage = useRef<HTMLDivElement>(null)
  const img = useRef<HTMLImageElement>(null)
  const closeButton = useRef<HTMLButtonElement>(null)
  const dialog = useRef<HTMLDivElement>(null)
  const pointers = useRef(new Map<number, Point>())
  const gesture = useRef<{ start: Point; offset: Point; distance?: number; scale: number } | null>(
    null,
  )
  const lastTap = useRef(0)
  /** Нажатие началось на тёмном поле, а не на фото (захват указателя подменяет target клика). */
  const onBackdrop = useRef(false)
  const many = images.length > 1

  const reset = useCallback(() => {
    setScale(1)
    setOffset({ x: 0, y: 0 })
  }, [])

  // Смена фото — масштаб сначала, оригинал заново по требованию (сброс прямо в рендере).
  const [shown, setShown] = useState(index)
  if (shown !== index) {
    setShown(index)
    setScale(1)
    setOffset({ x: 0, y: 0 })
    setHiRes(null)
  }
  const [dragging, setDragging] = useState(false)

  // Соседние фото — заранее: листание без ожидания загрузки.
  useEffect(() => {
    if (!many) return
    for (const delta of [1, -1]) {
      const next = images[(index + delta + images.length) % images.length]
      if (next) new Image().src = next.display
    }
  }, [many, images, index])

  // Оригинал грузим фоном и подменяем, когда он готов.
  useEffect(() => {
    if (!image || scale < HI_RES_FROM || hiRes === image.original) return
    const probe = new Image()
    probe.onload = () => setHiRes(image.original)
    probe.src = image.original
    return () => {
      probe.onload = null
    }
  }, [image, scale, hiRes])

  /** Сдвиг не даёт увести фото за край: край картинки не дальше края экрана. */
  const clamp = useCallback((next: Point, s: number): Point => {
    const box = stage.current?.getBoundingClientRect()
    const el = img.current
    if (!box || !el) return next
    const maxX = Math.max(0, (el.offsetWidth * s - box.width) / 2)
    const maxY = Math.max(0, (el.offsetHeight * s - box.height) / 2)
    return {
      x: Math.min(maxX, Math.max(-maxX, next.x)),
      y: Math.min(maxY, Math.max(-maxY, next.y)),
    }
  }, [])

  /** Зум к точке (координаты экрана): точка под пальцем/курсором остаётся на месте. */
  const zoomTo = useCallback(
    (next: number, at?: Point) => {
      const s = Math.min(MAX, Math.max(MIN, next))
      const box = stage.current?.getBoundingClientRect()
      if (s === 1 || !box) {
        setScale(s)
        setOffset(s === 1 ? { x: 0, y: 0 } : (o) => o)
        return
      }
      const p = at
        ? { x: at.x - box.left - box.width / 2, y: at.y - box.top - box.height / 2 }
        : { x: 0, y: 0 }
      setOffset((o) =>
        clamp({ x: p.x - (p.x - o.x) * (s / scale), y: p.y - (p.y - o.y) * (s / scale) }, s),
      )
      setScale(s)
    },
    [clamp, scale],
  )

  const go = useCallback(
    (delta: number) => {
      if (!many) return
      onIndexChange((index + delta + images.length) % images.length)
    },
    [many, index, images.length, onIndexChange],
  )

  // Клавиатура, блокировка прокрутки страницы, фокус внутрь и обратно.
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    closeButton.current?.focus()
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = overflow
      previous?.focus?.()
    }
  }, [])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') go(1)
      else if (e.key === 'ArrowLeft') go(-1)
      else if (e.key === '+' || e.key === '=') zoomTo(scale * STEP)
      else if (e.key === '-') zoomTo(scale / STEP)
      else if (e.key === '0') reset()
      else if (e.key === 'Tab') {
        // Фокус не уходит за окно на страницу под ним.
        const items = [
          ...(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href]') ??
            []),
        ]
        const at = items.indexOf(document.activeElement as HTMLElement)
        const next = (at + (e.shiftKey ? -1 : 1) + items.length) % items.length
        items[at < 0 ? 0 : next]?.focus()
      } else return
      e.preventDefault()
      e.stopPropagation()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose, go, zoomTo, scale, reset])

  // Колесо — с passive: false, чтобы не прокручивать страницу под окном.
  useEffect(() => {
    const el = stage.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      zoomTo(scale * Math.exp(-e.deltaY * 0.0015), { x: e.clientX, y: e.clientY })
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [zoomTo, scale])

  if (!image) return null

  const distance = () => {
    const [a, b] = [...pointers.current.values()]
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0
  }
  const middle = (): Point => {
    const [a, b] = [...pointers.current.values()]
    return a && b ? { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } : (a ?? { x: 0, y: 0 })
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return
    onBackdrop.current = e.target === e.currentTarget
    e.currentTarget.setPointerCapture(e.pointerId)
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    setDragging(true)
    gesture.current = {
      start: middle(),
      offset,
      scale,
      distance: pointers.current.size === 2 ? distance() : undefined,
    }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId) || !gesture.current) return
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    const g = gesture.current
    if (pointers.current.size === 2 && g.distance) {
      zoomTo(g.scale * (distance() / g.distance), middle())
      return
    }
    if (scale > 1) {
      const p = middle()
      setOffset(clamp({ x: g.offset.x + p.x - g.start.x, y: g.offset.y + p.y - g.start.y }, scale))
    }
  }
  const onPointerUp = (e: React.PointerEvent) => {
    const g = gesture.current
    const wasPinch = pointers.current.size === 2
    pointers.current.delete(e.pointerId)
    if (pointers.current.size) {
      // После щипка оставшийся палец продолжает сдвиг с текущего места.
      gesture.current = { start: middle(), offset, scale }
      return
    }
    gesture.current = null
    setDragging(false)
    if (!g || wasPinch) return
    const dx = e.clientX - g.start.x
    const dy = e.clientY - g.start.y
    // Жест с движением (свайп, сдвиг) — не клик по полю: окно после него не закрывается.
    if (Math.abs(dx) >= 6 || Math.abs(dy) >= 6) onBackdrop.current = false
    // Свайп — только когда фото не приближено.
    if (scale === 1 && Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      go(dx < 0 ? 1 : -1)
      return
    }
    // Двойной тап/клик: приблизить к точке или вернуть как было.
    if (Math.abs(dx) < 6 && Math.abs(dy) < 6) {
      const now = Date.now()
      if (now - lastTap.current < 300) {
        if (scale > 1) reset()
        else zoomTo(2.5, { x: e.clientX, y: e.clientY })
        lastTap.current = 0
      } else lastTap.current = now
    }
  }

  const button =
    'inline-flex size-10 items-center justify-center rounded-full bg-black/50 text-lg text-white transition-colors hover:bg-black/70 focus-visible:outline-2 focus-visible:outline-white disabled:opacity-40'

  return createPortal(
    <div
      ref={dialog}
      role="dialog"
      aria-modal="true"
      aria-label={image.alt}
      className="fixed inset-0 z-50 flex flex-col bg-neutral-950 text-white"
    >
      <div className="flex items-center gap-2 p-3">
        <p className="mr-auto min-w-0 truncate text-sm">
          {image.caption ?? image.alt}
          {many ? (
            <span className="ml-2 text-white/60">
              {index + 1} / {images.length}
            </span>
          ) : null}
        </p>
        <button
          type="button"
          className={button}
          aria-label={t('lightbox.zoomOut')}
          disabled={scale <= MIN}
          onClick={() => zoomTo(scale / STEP)}
        >
          −
        </button>
        <button
          type="button"
          className="min-w-14 rounded-full bg-black/50 px-2 py-2 text-xs tabular-nums hover:bg-black/70"
          aria-label={t('lightbox.reset')}
          onClick={reset}
        >
          {Math.round(scale * 100)}%
        </button>
        <button
          type="button"
          className={button}
          aria-label={t('lightbox.zoomIn')}
          disabled={scale >= MAX}
          onClick={() => zoomTo(scale * STEP)}
        >
          +
        </button>
        <a
          href={image.original}
          target="_blank"
          rel="noreferrer"
          className="hidden rounded-full bg-black/50 px-3 py-2 text-xs hover:bg-black/70 sm:inline-block"
        >
          {t('lightbox.original')}
        </a>
        <button
          ref={closeButton}
          type="button"
          className={button}
          aria-label={t('lightbox.close')}
          onClick={onClose}
        >
          ✕
        </button>
      </div>
      <div
        ref={stage}
        className="relative flex flex-1 touch-none items-center justify-center overflow-hidden select-none"
        style={{ cursor: scale > 1 ? 'grab' : 'zoom-in' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={() => {
          // Клик по пустому полю вокруг фото (не по самому фото) закрывает просмотр.
          if (onBackdrop.current && scale === 1) onClose()
          onBackdrop.current = false
        }}
      >
        <img
          ref={img}
          key={image.id}
          src={hiRes ?? image.display}
          alt={image.alt}
          draggable={false}
          className="max-h-full max-w-full object-contain"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            transition: dragging ? 'none' : 'transform 120ms ease-out',
          }}
        />
        {many ? (
          <>
            <button
              type="button"
              className={`${button} absolute top-1/2 left-3 -translate-y-1/2`}
              aria-label={t('lightbox.prev')}
              onClick={() => go(-1)}
            >
              ‹
            </button>
            <button
              type="button"
              className={`${button} absolute top-1/2 right-3 -translate-y-1/2`}
              aria-label={t('lightbox.next')}
              onClick={() => go(1)}
            >
              ›
            </button>
          </>
        ) : null}
      </div>
      <p className="p-2 text-center text-xs text-white/50">{t('lightbox.hint')}</p>
    </div>,
    document.body,
  )
}
